import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Cpu,
  Package,
  CalendarCheck,
  ClipboardList,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  Factory,
  FileSpreadsheet,
  Shield,
  ScrollText,
  ArrowLeftRight,
  SlidersHorizontal,
  FileText,
  Tag,
  Monitor,
  Megaphone,
  Film,
  Radio,
  TrendingUp,
  Activity
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { supabase, signOut } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { useDarkMode } from '@/hooks/useDarkMode';

import { useCurrentUser } from '@/lib/useCurrentUser';
import { canAccessVideoPlaylists } from '@/lib/roles';

// Navigation items with role-based access
const navigation: { name: string; href: string; icon: any; roles: string[]; divider?: boolean }[] = [
  // Home
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, roles: ['super_admin', 'admin'] },
  // Build lifecycle
  { name: 'Production', href: '/production', icon: Factory, roles: ['super_admin', 'admin'], divider: true },
  { name: 'Reservations', href: '/reservations', icon: CalendarCheck, roles: ['super_admin', 'admin'] },
  { name: 'Assembly Orders', href: '/assembly-orders', icon: ClipboardList, roles: ['super_admin', 'admin', 'manager'] },
  { name: 'Posted Assembly Orders', href: '/stock-io', icon: FileSpreadsheet, roles: ['super_admin', 'admin', 'manager', 'technician'] },
  // Catalogue
  { name: 'Components', href: '/components', icon: Cpu, roles: ['super_admin', 'admin', 'manager', 'technician'], divider: true },
  { name: 'Builds', href: '/builds', icon: Package, roles: ['super_admin', 'admin', 'manager', 'technician', 'marketing', 'retail'] },
  // Reporting
  { name: 'Sales Report', href: '/sales-report', icon: TrendingUp, roles: ['super_admin', 'admin', 'manager'], divider: true },
  // Tools
  { name: 'Configurator', href: '/configurator', icon: SlidersHorizontal, roles: ['super_admin', 'admin'], divider: true },
  { name: 'Compare', href: '/compare', icon: ArrowLeftRight, roles: ['super_admin', 'admin'] },
  { name: 'Quote', href: '/quote', icon: FileText, roles: ['super_admin', 'admin'] },
  { name: 'Print Labels', href: '/print-labels', icon: Tag, roles: ['super_admin', 'admin', 'manager'] },
  // Demo / Marketing (admin group)
  { name: 'Demo Stations', href: '/demo-stations', icon: Monitor, roles: ['super_admin', 'admin', 'marketing'], divider: true },
  { name: 'Ad Campaigns', href: '/ad-campaigns', icon: Megaphone, roles: ['super_admin', 'admin', 'marketing'] },
  { name: 'Video Playlists', href: '/video-playlists', icon: Film, roles: ['super_admin', 'admin', 'marketing'] },
  { name: 'Social Mentions', href: '/mentions', icon: Radio, roles: ['super_admin', 'admin', 'marketing'] },
  // Technician
  { name: 'My Orders', href: '/my-orders', icon: ClipboardList, roles: ['technician'] },
  { name: 'Print Labels', href: '/print-labels', icon: Tag, roles: ['technician'] },
  { name: 'Demo Stations', href: '/demo-stations', icon: Monitor, roles: ['technician', 'retail', 'retail_shop'] },
  { name: 'Video Playlists', href: '/video-playlists', icon: Film, roles: ['technician'] },
];

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const queryClient = useQueryClient();
  const { isDark } = useDarkMode();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved ? JSON.parse(saved) : false;
  });

  // Fetch current user's role and name — see @/lib/useCurrentUser
  const { data: userData, isLoading: isUserLoading } = useCurrentUser();

  const userRole = userData?.role;
  const userName = userData?.name;
  const userInitials = userData?.initials || userName?.charAt(0)?.toUpperCase() || '?';

  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', JSON.stringify(collapsed));
  }, [collapsed]);

  // ── Supabase Realtime — invalidate queries when DB rows change ──────────────
  useEffect(() => {
    const channel = supabase
      .channel('global-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'reservations' }, () => {
        queryClient.invalidateQueries({ queryKey: ['reservations'] });
        queryClient.invalidateQueries({ queryKey: ['reservations-enriched'] });
        queryClient.invalidateQueries({ queryKey: ['reservation'] });
        queryClient.invalidateQueries({ queryKey: ['production-reservations'] });
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'assembly_orders' }, () => {
        queryClient.invalidateQueries({ queryKey: ['active-assembly-orders'] });
        queryClient.invalidateQueries({ queryKey: ['assembly-order-status-counts'] });
        queryClient.invalidateQueries({ queryKey: ['production-orders'] });
        queryClient.invalidateQueries({ queryKey: ['technician-orders'] });
        queryClient.invalidateQueries({ queryKey: ['reservation-build-orders'] });
        queryClient.invalidateQueries({ queryKey: ['latest-order-number'] });
        queryClient.invalidateQueries({ queryKey: ['awaiting-review-count'] });
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'posted_assembly_orders' }, () => {
        queryClient.invalidateQueries({ queryKey: ['posted-assembly-orders'] });
        queryClient.invalidateQueries({ queryKey: ['production-completed-week'] });
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'components' }, () => {
        queryClient.invalidateQueries({ queryKey: ['components'] });
        queryClient.invalidateQueries({ queryKey: ['comp-stock-live'] });
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'reservation_components' }, () => {
        queryClient.invalidateQueries({ queryKey: ['reservation'] });
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  const handleSignOut = async () => {
    queryClient.clear(); // Clear all cached data including role
    await signOut();
  };

  // Orders waiting for an admin's review, shown as a bubble on Production.
  // Kept live by the assembly_orders realtime subscription above.
  const canReview = userRole === 'super_admin' || userRole === 'admin';
  const { data: awaitingReviewCount = 0 } = useQuery({
    queryKey: ['awaiting-review-count'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('assembly_orders')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'awaiting_review');
      if (error) throw error;
      return count ?? 0;
    },
    enabled: canReview,
  });
  const navBadges: Record<string, { count: number; label: string }> = {
    '/production': { count: awaitingReviewCount, label: 'awaiting review' },
  };

  // Filter navigation items based on role - show nothing while loading
  const filteredNavigation = isUserLoading 
    ? [] 
    : !userRole 
      ? [] 
      : navigation.filter(item =>
          item.roles.includes(userRole) &&
          // A technician's Video Playlists entry also needs their individual grant.
          (item.href !== '/video-playlists' || canAccessVideoPlaylists(userRole, userData?.canManagePlaylists ?? false)));

  // Show admin link only for super_admin
  const showAdminLink = userRole === 'super_admin';
  
  // Check if user is technician or manager for dashboard link
  const showTechDashboard = userRole === 'technician';
  const showManagerDashboard = userRole === 'manager' || userRole === 'super_admin' || userRole === 'admin';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-gray-900/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed inset-y-0 left-0 z-50 bg-white dark:bg-gray-900 border-r dark:border-gray-800 transform transition-all duration-200 ease-in-out lg:translate-x-0",
        sidebarOpen ? "translate-x-0 w-64" : "-translate-x-full w-64",
        collapsed ? "lg:w-[68px]" : "lg:w-64"
      )}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center h-16 px-2 border-b overflow-hidden">
            <Link to={userRole === 'retail_shop' ? '/demo-stations' : userRole === 'technician' ? '/my-dashboard' : userRole === 'manager' ? '/team-dashboard' : '/dashboard'} className={cn(
              "flex items-center transition-all duration-200",
              collapsed ? "w-full justify-center" : "flex-1 justify-center"
            )}>
              <img src={isDark ? "/logo-dark.png" : "/logo-horizontal.png"} alt="AOI Gaming" className={cn(
                "w-auto flex-shrink-0 transition-all duration-200",
                collapsed ? "h-7" : "h-10"
              )} />
            </Link>
            <button
              className="lg:hidden ml-auto flex-shrink-0"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto overflow-x-hidden">
            {/* Show loading skeleton while fetching role */}
            {isUserLoading ? (
              <div className="space-y-2 px-3">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="h-9 bg-gray-100 dark:bg-gray-800 rounded-md animate-pulse" />
                ))}
              </div>
            ) : (
              <>
                {/* Technician Dashboard */}
                {showTechDashboard && (
                  <Link
                    to="/my-dashboard"
                    className={cn(
                      "flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors overflow-hidden",
                      location.pathname === '/my-dashboard'
                        ? "bg-primary text-primary-foreground"
                        : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                    )}
                    onClick={() => setSidebarOpen(false)}
                    title={collapsed ? "My Dashboard" : undefined}
                  >
                    <LayoutDashboard className="h-5 w-5 flex-shrink-0" />
                    <span className={cn(
                      "ml-3 whitespace-nowrap transition-all duration-200",
                      collapsed ? "opacity-0 w-0" : "opacity-100"
                    )}>
                      My Dashboard
                    </span>
                  </Link>
                )}

                {/* Manager / Admin Team Dashboard */}
                {showManagerDashboard && (
                  <Link
                    to="/team-dashboard"
                    className={cn(
                      "flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors overflow-hidden",
                      location.pathname === '/team-dashboard'
                        ? "bg-primary text-primary-foreground"
                        : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                    )}
                    onClick={() => setSidebarOpen(false)}
                    title={collapsed ? "Team Dashboard" : undefined}
                  >
                    <LayoutDashboard className="h-5 w-5 flex-shrink-0" />
                    <span className={cn(
                      "ml-3 whitespace-nowrap transition-all duration-200",
                      collapsed ? "opacity-0 w-0" : "opacity-100"
                    )}>
                      Team Dashboard
                    </span>
                  </Link>
                )}

                {filteredNavigation.map((item) => {
                  const isActive = location.pathname.startsWith(item.href);
                  const badge = navBadges[item.href];
                  const badgeText = badge && badge.count > 0 ? `${badge.count} ${badge.label}` : null;
                  return (
                    <div key={item.name}>
                    {item.divider && <hr className="my-2 border-gray-200 dark:border-gray-700" />}
                    <Link
                      to={item.href}
                      className={cn(
                        "flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors overflow-hidden",
                        isActive
                          ? "bg-primary text-primary-foreground"
                          : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                      )}
                      onClick={() => setSidebarOpen(false)}
                      title={collapsed ? (badgeText ? `${item.name} · ${badgeText}` : item.name) : undefined}
                    >
                      <span className="relative flex-shrink-0">
                        <item.icon className="h-5 w-5" />
                        {/* Collapsed sidebar: a dot on the icon stands in for the count */}
                        {badgeText && collapsed && (
                          <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white dark:ring-gray-900" />
                        )}
                      </span>
                      <span className={cn(
                        "ml-3 whitespace-nowrap transition-all duration-200",
                        collapsed ? "opacity-0 w-0" : "opacity-100"
                      )}>
                        {item.name}
                      </span>
                      {badgeText && !collapsed && (
                        <span
                          className="ml-auto min-w-5 rounded-full bg-red-500 px-1.5 text-center text-[11px] font-semibold leading-5 text-white"
                          title={badgeText}
                          aria-label={badgeText}
                        >
                          {badge.count}
                        </span>
                      )}
                    </Link>
                    </div>
                  );
                })}

                {/* Admin link - only for super_admin */}
                {showAdminLink && (
                  <Link
                    to="/admin"
                    className={cn(
                      "flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors overflow-hidden",
                      location.pathname.startsWith('/admin')
                        ? "bg-primary text-primary-foreground"
                        : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                    )}
                    onClick={() => setSidebarOpen(false)}
                    title={collapsed ? "User Management" : undefined}
                  >
                    <Shield className="h-5 w-5 flex-shrink-0" />
                    <span className={cn(
                      "ml-3 whitespace-nowrap transition-all duration-200",
                      collapsed ? "opacity-0 w-0" : "opacity-100"
                    )}>
                      User Management
                    </span>
                  </Link>
                )}
                {showAdminLink && (
                  <Link
                    to="/logs"
                    className={cn(
                      "flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors overflow-hidden",
                      location.pathname.startsWith('/logs')
                        ? "bg-primary text-primary-foreground"
                        : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                    )}
                    onClick={() => setSidebarOpen(false)}
                    title={collapsed ? "Audit Log" : undefined}
                  >
                    <ScrollText className="h-5 w-5 flex-shrink-0" />
                    <span className={cn(
                      "ml-3 whitespace-nowrap transition-all duration-200",
                      collapsed ? "opacity-0 w-0" : "opacity-100"
                    )}>
                      Audit Log
                    </span>
                  </Link>
                )}
                {showAdminLink && (
                  <Link
                    to="/activity"
                    className={cn(
                      "flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors overflow-hidden",
                      location.pathname.startsWith('/activity')
                        ? "bg-primary text-primary-foreground"
                        : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                    )}
                    onClick={() => setSidebarOpen(false)}
                    title={collapsed ? "User Activity" : undefined}
                  >
                    <Activity className="h-5 w-5 flex-shrink-0" />
                    <span className={cn(
                      "ml-3 whitespace-nowrap transition-all duration-200",
                      collapsed ? "opacity-0 w-0" : "opacity-100"
                    )}>
                      User Activity
                    </span>
                  </Link>
                )}
              </>
            )}
          </nav>

          {/* Collapse toggle (desktop only) */}
          <div className="hidden lg:flex items-center h-11 px-2 border-t">
            <button
              onClick={() => setCollapsed(!collapsed)}
              className={cn(
                "flex items-center h-9 w-full rounded-md hover:bg-gray-100 transition-colors",
                collapsed ? "justify-center px-0" : "px-3"
              )}
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {collapsed ? (
                <ChevronRight className="h-5 w-5 flex-shrink-0" />
              ) : (
                <>
                  <ChevronLeft className="h-5 w-5 flex-shrink-0" />
                  <span className="ml-3 whitespace-nowrap text-sm font-medium text-gray-700">Collapse</span>
                </>
              )}
            </button>
          </div>

          {/* User info - always visible with initials */}
          <div className={cn(
            "flex items-center h-14 border-t",
            collapsed ? "justify-center px-2" : "px-3"
          )}>
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-semibold text-primary">
                {isUserLoading ? '?' : userInitials}
              </span>
            </div>
            {!collapsed && (
              <div className="ml-2 overflow-hidden">
                <p className="text-sm font-medium truncate">
                  {isUserLoading ? 'Loading...' : (userName || 'User')}
                </p>
                <p className="text-xs text-muted-foreground capitalize">
                  {isUserLoading ? '' : (userRole?.replace('_', ' ') || '')}
                </p>
              </div>
            )}
          </div>

          {/* Sign out */}
          <div className={cn(
            "flex items-center h-11 border-t",
            collapsed ? "justify-center px-2" : "px-2"
          )}>
            <Button 
              variant="ghost" 
              className={cn(
                "h-9 w-full",
                collapsed ? "justify-center px-0" : "justify-start px-3"
              )}
              onClick={handleSignOut}
              title={collapsed ? "Sign Out" : undefined}
            >
              <LogOut className="h-5 w-5 flex-shrink-0" />
              {!collapsed && (
                <span className="ml-3 whitespace-nowrap">
                  Sign Out
                </span>
              )}
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className={cn(
        "transition-all duration-200",
        collapsed ? "lg:pl-[68px]" : "lg:pl-64"
      )}>
        {/* Mobile header */}
        <header className="sticky top-0 z-30 flex items-center h-16 px-4 bg-white dark:bg-gray-900 border-b dark:border-gray-800 lg:hidden">
          <button onClick={() => setSidebarOpen(true)}>
            <Menu className="h-6 w-6" />
          </button>
          <img src={isDark ? "/logo-dark.png" : "/logo-horizontal.png"} alt="AOI Gaming" className="h-8 w-auto ml-3" />
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
